import React, { useState, useEffect } from 'react'

// Antd
import { Button, Col, Drawer, Form, Input, Row, Select, notification, Spin } from 'antd';
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import { checkIsEmailExist, createUser, getAllCenters } from '../../../../Constants/Api/functions/function';
import { useSelector } from 'react-redux';

const { Option } = Select;

const AddUser = ({ setOpen, isOpen, handleGetAllUsers }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [centers, setCenters] = useState([])
    const [centersLoading, setCentersLoading] = useState(false)
    const [api, contextHolder] = notification.useNotification();
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const [form] = Form.useForm();

    useEffect(() => {
        if (isOpen) {
            handleGetAllCenters();
            // If user is admin, set their centerId automatically
            if (userProfile?.role === 'admin' && userProfile?.centerId) {
                form.setFieldsValue({ centerId: userProfile.centerId });
            }
        }
    }, [isOpen, userProfile]);

    const handleGetAllCenters = async () => {
        try {
            setCentersLoading(true);
            const response = await getAllCenters();
            // getAllCenters() already returns response.data (the array), not the full response object
            if (response && Array.isArray(response)) {
                setCenters(response);
            } else if (response && response.data && Array.isArray(response.data)) {
                // Fallback in case the response structure is different
                setCenters(response.data);
            }
        } catch (error) {
            console.error('Error fetching centers:', error);
        } finally {
            setCentersLoading(false);
        }
    };

    const onFinish = async (values) => {
        Object.keys(values).forEach(key => (values[key] === undefined || values[key] === '') && delete values[key]);

        try {
            setIsLoading(true)

            // Remove confirmPassword from values before sending to API
            delete values.confirmPassword;

            // If admin is creating user, automatically assign their center
            if (userProfile?.role === 'admin' && userProfile?.centerId && !values.centerId) {
                values.centerId = userProfile.centerId;
            }

            const isEmailExist = await checkIsEmailExist(values.email)

            if (isEmailExist) {
                emailExistNotification()
                setIsLoading(false)
            } else {
                const response = await createUser(values)
                if (response && (response?.status === 200 || response?.status === 201)) {
                    openNotificationWithIcon("success")
                    closeAddUserDrawer()
                    form.resetFields();
                    await handleGetAllUsers()
                } else {
                    console.log("error", response?.data?.message)
                    openNotificationWithIcon("error", response?.data?.message)
                }
            }
        } catch (error) {
            console.log("error", error)
            openNotificationWithIcon("error", error)
        }
        setIsLoading(false)
    }

    const openNotificationWithIcon = (type, error) => {
        api[type]({
            message: type === "success" ? "User Created Successfully" : "Error Creating User",
            description: type === "success" 
                ? "User has been created successfully. " + (form.getFieldValue('password') 
                    ? "The user can now login with the provided password." 
                    : "Email has been sent to the user with login credentials.")
                : `There was an error creating user. Please try again later. ${error}`
        });
    };

    const emailExistNotification = () => {
        api['error']({
            message: "Email already exist",
            description: "The email you entered already exist. Please enter a different email."
        });
    }


    const closeAddUserDrawer = () => {
        setOpen(false)
        form.resetFields();
    }

    return (
        <div>
            {contextHolder}
            <Drawer
                title="Create new user"
                width={720}
                onClose={closeAddUserDrawer}
                open={isOpen}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                <Spin
                    spinning={isLoading}
                    tip="Creating user..."
                >
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        form={form}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label="User Name"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter user's name",
                                        },
                                    ]}
                                >
                                    <Input placeholder="Please enter user's name" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter user email',
                                        },
                                        {
                                            type: 'email',
                                        }
                                    ]}
                                >
                                    <Input placeholder="Please enter user email" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    name="password"
                                    label="Password"
                                    rules={[
                                        {
                                            min: 6,
                                            message: 'Password must be at least 6 characters!',
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="Enter password (optional)"
                                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    label="Confirm Password"
                                    name="confirmPassword"
                                    dependencies={['password']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || !getFieldValue('password') || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The passwords do not match!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="Confirm password (optional)"
                                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    label="Contact Number"
                                    name="contactNumber"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please input your contact number!',
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || /^0[0-9]{9}$/.test(value)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('The input is not a valid phone number (e.g., 0XX XXXX XXX)!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input placeholder="0XX XXXX XXX" />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    label="Role"
                                    name="role"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select user role!',
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Select user role"
                                        allowClear
                                    >
                                        {userProfile?.role === 'superadmin' && (
                                            <Option value="superadmin">Super Admin</Option>
                                        )}
                                        <Option value="admin">Admin</Option>
                                        <Option value="coordinator">Coordinator</Option>
                                        <Option value="student">Student</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    label="Address"
                                    name="address"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please input user address!',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter user address" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    className='form-item'
                                    label="Center"
                                    name="centerId"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a center!',
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Select a center"
                                        loading={centersLoading}
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        disabled={userProfile?.role === 'admin'}
                                        options={centers.map(center => ({
                                            value: center.CenterId,
                                            label: center.CenterName,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Button type="primary" htmlType="submit">
                                    Add User
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Drawer>
        </div>
    )
}

export default AddUser